/**
 * AuditDetailDialog — side-by-side JSON view of the `payload_before` and
 * `payload_after` of a single audit row.
 *
 * MVP uses a plain `<pre>` with pre-formatted JSON. A full diff lib is a
 * follow-up enhancement; for now the side-by-side presentation plus the
 * metadata header is enough to inspect who-did-what.
 */

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { formatDateTime } from '@/lib/format-date';

export interface AuditDetailDto {
  id: string;
  adminUserId: string | null;
  actorEmail: string | null;
  action: string;
  resourceType: string;
  resourceId: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  correlationId: string | null;
  createdAt: string;
  payloadBefore: unknown;
  payloadAfter: unknown;
}

interface Props {
  open: boolean;
  entry: AuditDetailDto | null;
  onClose: () => void;
}

function formatJson(v: unknown): string {
  if (v === null || v === undefined) return '—';
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

export function AuditDetailDialog({ open, entry, onClose }: Props) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="flex max-h-[90vh] w-[calc(100vw-2rem)] flex-col gap-4 overflow-y-auto sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>Detalhe da ação</DialogTitle>
          <DialogDescription>
            {entry ? (
              <>
                <span className="font-mono">{entry.action}</span> por{' '}
                <span className="font-mono">{entry.actorEmail ?? entry.adminUserId ?? '—'}</span> em{' '}
                {formatDateTime(entry.createdAt)}
              </>
            ) : null}
          </DialogDescription>
        </DialogHeader>
        {entry ? (
          <div className="space-y-3">
            <dl className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <dt className="text-muted-foreground">Resource</dt>
                <dd className="font-mono">
                  {entry.resourceType}
                  {entry.resourceId ? ` / ${entry.resourceId}` : ''}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">IP</dt>
                <dd className="font-mono">{entry.ipAddress ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Correlation</dt>
                <dd className="font-mono">{entry.correlationId ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">User-Agent</dt>
                <dd className="font-mono truncate">{entry.userAgent ?? '—'}</dd>
              </div>
            </dl>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <h3 className="mb-1 text-xs font-semibold uppercase text-muted-foreground">
                  Payload before
                </h3>
                <pre
                  data-testid="audit-payload-before"
                  className="max-h-96 overflow-auto rounded-md border bg-red-500/5 p-2 font-mono text-xs"
                >
                  {formatJson(entry.payloadBefore)}
                </pre>
              </div>
              <div>
                <h3 className="mb-1 text-xs font-semibold uppercase text-muted-foreground">
                  Payload after
                </h3>
                <pre
                  data-testid="audit-payload-after"
                  className="max-h-96 overflow-auto rounded-md border bg-emerald-500/5 p-2 font-mono text-xs"
                >
                  {formatJson(entry.payloadAfter)}
                </pre>
              </div>
            </div>
          </div>
        ) : null}
        <DialogFooter>
          <Button onClick={onClose}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
